import { type FormEvent, useState } from 'react';
import { useOrgData } from '../accountant/useOrgData.js';
import type { Api } from '../api.js';

interface AssetAccount {
  id: string;
  code: string;
  name: string;
  type: string;
  subtype: string | null;
  archived: boolean;
}

export function AddBankAccount({
  api,
  orgId,
  onAdded,
}: {
  api: Api;
  orgId: string;
  onAdded: () => void;
}) {
  const { data } = useOrgData<{ accounts: AssetAccount[] }>(api, orgId, '/accounts');
  const banks = (data?.accounts ?? []).filter(
    (a) => a.type === 'asset' && a.subtype === 'bank' && !a.archived,
  );
  const [name, setName] = useState('');
  const [institution, setInstitution] = useState('Bank Australia');
  const [last4, setLast4] = useState('');
  const [ledgerAccountId, setLedgerAccountId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const chosen = ledgerAccountId || banks[0]?.id || '';

  async function add(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api.send(orgId, 'POST', '/bank-accounts', {
        name: name || banks.find((b) => b.id === chosen)?.name,
        institution,
        accountNumber: last4,
        ledgerAccountId: chosen,
      });
      onAdded();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the bank account.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={add} className="stack">
      <h2>Add a bank account</h2>
      <label>
        Which account is it?
        <select value={chosen} onChange={(e) => setLedgerAccountId(e.target.value)} required>
          {banks.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </label>
      <label>
        Bank
        <select value={institution} onChange={(e) => setInstitution(e.target.value)}>
          <option>Bank Australia</option>
          <option value="Other">Another bank</option>
        </select>
      </label>
      <label>
        Last 4 digits of the account number (optional, used to catch the wrong file)
        <input
          inputMode="numeric"
          maxLength={4}
          value={last4}
          onChange={(e) => setLast4(e.target.value.replace(/\D/g, ''))}
        />
      </label>
      <label>
        Name (optional)
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={banks.find((b) => b.id === chosen)?.name}
        />
      </label>
      {error && <p className="error">{error}</p>}
      <button type="submit" disabled={busy || !chosen}>
        Add
      </button>
    </form>
  );
}
